import React, { useState } from "react";
import requestJson from "../../shared/request-json";

// Иконки в стиле остальных компонентов проекта (набор Lucide, локальный хелпер).
const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);

const ICON_MAIL = <Icon d={<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></>} />;
const ICON_LOCK = <Icon d={<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>} />;
const ICON_EYE = <Icon d={<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></>} />;
const ICON_EYE_OFF = <Icon d={<><path d="M10.7 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-3.2 4.2" /><path d="M6.6 6.6A18.6 18.6 0 0 0 2 12s3.5 7 10 7a10.8 10.8 0 0 0 4.5-.9" /><path d="m2 2 20 20" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>} />;
const ICON_ALERT = <Icon d={<><circle cx="12" cy="12" r="10" /><path d="M12 8v5" /><path d="M12 16h.01" /></>} size={16} />;
const ICON_CHECK = <Icon d={<><circle cx="12" cy="12" r="10" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></>} size={16} />;
const ICON_ARROW = <Icon d={<><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>} />;
const ICON_DOT = <Icon d={<><circle cx="12" cy="12" r="9" /><path d="m8.5 12.2 2.4 2.4 4.6-5" /></>} />;

const BRAND_POINTS = [
  "Заявки, назначения и план на день",
  "Учёт часов, ГСМ и штрафов",
  "Ротация техники и экономика объектов"
];

const SessionPage = ({ sessionPath, newPasswordPath, alert: initialAlert, notice: initialNotice }) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  // Флеш из редиректа гасим, как только пользователь начал новую попытку.
  const [flashAlert, setFlashAlert] = useState(initialAlert);
  const [flashNotice, setFlashNotice] = useState(initialNotice);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);
    setFlashAlert(null);
    setFlashNotice(null);
    try {
      const data = await requestJson(sessionPath, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ email_address: emailAddress, password })
      });
      if (data?.redirect_url) {
        window.location = data.redirect_url;
        return;
      }
    } catch (err) {
      if (err.status === 429) {
        setErrors(err.payload?.errors || ["Слишком много попыток входа. Повторите через несколько минут."]);
      } else if (err.status) {
        setErrors(err.payload?.errors || ["Неверная почта или пароль."]);
      } else {
        setErrors(["Не удалось связаться с сервером. Попробуйте ещё раз."]);
      }
    } finally {
      setLoading(false);
    }
  };

  const alertList = [...(flashAlert ? [flashAlert] : []), ...errors];

  return (
    <div className="auth-split">
      <aside className="auth-brand">
        <div className="auth-brand-inner">
          <div className="auth-brand-logo">
            <img src="/icon.svg" alt="" className="auth-brand-mark" />
            <span className="auth-brand-name">
              Оксбур
              <span className="auth-brand-role">Диспетчерская техники</span>
            </span>
          </div>

          <div className="auth-brand-body">
            <h2 className="auth-brand-title">Весь парк — на одном экране</h2>
            <p className="auth-brand-text">
              Заявки от участков, назначения водителей, отработанные часы и расходы
              собираются в одном месте. Данные с техники приходят автоматически —
              вручную ничего сводить не нужно.
            </p>

            <ul className="auth-brand-list">
              {BRAND_POINTS.map((point) => (
                <li key={point} className="auth-brand-item">{ICON_DOT}<span>{point}</span></li>
              ))}
            </ul>
          </div>

          <div className="auth-brand-foot">
            Доступ выдаёт администратор вашей организации.
          </div>
        </div>
      </aside>

      <main className="auth-pane">
        <div className="auth-card">
          <div className="auth-card-logo">
            <img src="/icon.svg" alt="" className="auth-card-mark" />
            <span className="auth-card-brand">Оксбур</span>
          </div>

          <div className="auth-card-head">
            <h1 className="auth-title">Вход в систему</h1>
            <p className="auth-subtitle">Введите рабочую почту и пароль</p>
          </div>

          {flashNotice && (
            <div className="auth-banner auth-banner--notice" role="status">
              {ICON_CHECK}<span>{flashNotice}</span>
            </div>
          )}

          {alertList.length > 0 && (
            <div className="auth-banner auth-banner--alert" role="alert">
              {ICON_ALERT}
              <ul>{alertList.map((msg, i) => <li key={i}>{msg}</li>)}</ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-fields" noValidate>
            <div className="auth-field">
              <label htmlFor="session_email_address" className="auth-label">Почта</label>
              <div className="auth-control">
                <span className="auth-control-icon">{ICON_MAIL}</span>
                <input
                  id="session_email_address"
                  type="email"
                  name="email_address"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  autoFocus
                  autoComplete="username"
                  inputMode="email"
                  placeholder="ivanov@company.ru"
                  className="auth-input"
                />
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="session_password" className="auth-label">Пароль</label>
              <div className="auth-control">
                <span className="auth-control-icon">{ICON_LOCK}</span>
                <input
                  id="session_password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Введите пароль"
                  className="auth-input auth-input--with-toggle"
                />
                <button
                  type="button"
                  className="auth-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? ICON_EYE_OFF : ICON_EYE}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? <><span className="auth-spinner" />Входим…</> : <>Войти{ICON_ARROW}</>}
            </button>
          </form>

          <div className="auth-foot">
            <a href={newPasswordPath} className="auth-link">Забыли пароль?</a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SessionPage;
